import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    debounceTime,
    filter,
    pairwise,
    startWith,
    takeWhile,
    tap
} from 'rxjs/operators';
import * as fromLookup from '../../../../lookups';
import { LoggingService } from '../../../../shared/services/logging';
import { LocaleEnum } from '../../../expense-enums';
import { City } from '../../../store/city/city.model';

@Component({
    selector: 'ef-expense-detail-city-state-zip',
    templateUrl: './expense-detail-city-state-zip.component.html',
    styleUrls: ['./expense-detail-city-state-zip.component.scss'],
    standalone: false
})
export class ExpenseDetailCityStateZipComponent
    implements AfterViewInit, OnChanges, OnDestroy
{
    public formDomestic: UntypedFormGroup;
    public formForeign: UntypedFormGroup;
    public formOCONUS: UntypedFormGroup;
    @Input() cities: City[];
    @Input() states: fromLookup.IState[];
    @Input() countries: fromLookup.ILocation[];
    @Input() areas: fromLookup.ILocation[];
    @Input() locations: fromLookup.ILocation[];
    @Input() selectedCity: City;
    @Input() selectedState: fromLookup.IState;
    @Input() selectedPostalCode: string;
    @Input() selectedCountryArea: fromLookup.ILocation;
    @Input() selectedLocation: fromLookup.ILocation;
    @Input() locale: LocaleEnum;
    @Output() SelectState = new EventEmitter<fromLookup.IState>();
    @Output() SelectCountryArea = new EventEmitter<string>();
    @Output() SelectCity = new EventEmitter<City>();
    @Output() SelectPostalCode = new EventEmitter<string>();
    @Output() SelectCountry = new EventEmitter<fromLookup.ILocation>();
    @Output() SelectLocation = new EventEmitter<fromLookup.ILocation>();
    @Output() Close = new EventEmitter<any>();
    @Input() showAddressDialog: boolean;
    @Output() showAddressDialogChange = new EventEmitter<boolean>();
    private active = true;
    public get CountryArea(): string {
        return this.locale === LocaleEnum.Foreign ? 'Country' : 'Area';
    }
    public LocaleEnum: any;
    constructor(private log: LoggingService) {
        this.formDomestic = new UntypedFormGroup({
            PostalCode: new UntypedFormControl(),
            State: new UntypedFormControl(null, Validators.required),
            City: new UntypedFormControl(null, Validators.required)
        });
        this.formForeign = new UntypedFormGroup({
            CountryArea: new UntypedFormControl(null, Validators.required),
            Location: new UntypedFormControl(null, Validators.required)
        });
        this.formOCONUS = new UntypedFormGroup({
            CountryArea: new UntypedFormControl(null, Validators.required),
            Location: new UntypedFormControl(null, Validators.required)
        });
        this.LocaleEnum = LocaleEnum;
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    ngOnChanges(changes: SimpleChanges): void {
        this.log.verbose({
            type: 'ExpenseDetailCityStateZipComponent:ngOnChanges',
            changes
        });
        const controlsDomestic = this.formDomestic.controls;
        const controlsForeign = this.formForeign.controls;
        const controlsOCONUS = this.formOCONUS.controls;
        // if (changes && changes.locale) {
        //     this.setValidators(changes.locale.currentValue);
        // }
        if (
            changes &&
            changes.selectedState &&
            changes.selectedState.currentValue
        ) {
            controlsDomestic.State.setValue(changes.selectedState.currentValue);
        }
        if (
            changes &&
            changes.selectedCountryArea &&
            changes.selectedCountryArea.currentValue &&
            changes.selectedCountryArea.currentValue.CountryArea
        ) {
            controlsForeign.CountryArea.setValue(
                changes.selectedCountryArea.currentValue
            );
            controlsOCONUS.CountryArea.setValue(
                changes.selectedCountryArea.currentValue
            );
        }
        if (
            changes &&
            changes.selectedLocation &&
            changes.selectedLocation.currentValue &&
            changes.selectedLocation.currentValue.Destination
        ) {
            controlsForeign.Location.setValue(
                changes.selectedLocation.currentValue
            );
            controlsOCONUS.Location.setValue(
                changes.selectedLocation.currentValue
            );
        }
        if (
            changes &&
            changes.selectedCity &&
            changes.selectedCity.currentValue &&
            changes.selectedCity.currentValue.City
        ) {
            controlsDomestic.City.setValue(changes.selectedCity.currentValue);
        }
        if (changes && changes.selectedPostalCode) {
            controlsDomestic.PostalCode.setValue(
                changes.selectedPostalCode.currentValue
            );
        }
        if (changes && changes.locations) {
            if (
                changes.locations.currentValue &&
                changes.locations.currentValue.length === 1
            ) {
                controlsForeign.Location.setValue(
                    changes.locations.currentValue[0]
                );
            }
        }
        if (changes && changes.states) {
            if (
                changes.states.currentValue &&
                changes.states.currentValue.length === 1
            ) {
                controlsDomestic.State.setValue(changes.states.currentValue[0]);
            }
        }
        if (changes && changes.cities) {
            if (
                changes.cities.currentValue &&
                changes.cities.currentValue.length === 1
            ) {
                controlsDomestic.City.setValue(changes.cities.currentValue[0]);
            }
        }
    }
    ngAfterViewInit(): void {
        const controlsDomestic = this.formDomestic.controls;
        const controlsForeign = this.formForeign.controls;
        const controlsOCONUS = this.formOCONUS.controls;
        controlsDomestic.PostalCode.valueChanges
            .pipe(takeWhile(() => this.active))
            .subscribe(value => {
                controlsDomestic['State'].setValue(null, { emitEvent: false });
                controlsDomestic['City'].setValue(null, { emitEvent: false });
            });
        controlsDomestic.PostalCode.valueChanges
            .pipe(
                debounceTime(750),
                takeWhile(() => this.active)
            )
            .subscribe(value => {
                this.log.verbose({
                    type: 'ExpenseDetailCityStateZipComponent:onSelectPostalCode',
                    value
                });
                this.SelectPostalCode.emit(value);
            });
        controlsDomestic.PostalCode.valueChanges
            .pipe(
                takeWhile(() => this.active),
                filter(value => !value)
            )
            .subscribe(value => controlsDomestic.State.setValue(null));

        // clear the city when the state changes
        controlsDomestic.State.valueChanges
            .pipe(
                takeWhile(() => this.active),
                startWith(this.selectedState),
                tap(state =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipComponent:controlsDomestic.State.valueChanges',
                        state
                    })
                ),
                pairwise(),
                tap(([prev, next]) =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipComponent:controlsDomestic.State.valueChanges',
                        prev,
                        next
                    })
                ),
                filter(([prev, next]) => !!prev && !!next),
                filter(([prev, next]) => prev !== next)
            )
            .subscribe(([prev, next]) => {
                this.log.verbose({
                    type: 'ExpenseDetailCityStateZipComponent:State:valueChanges',
                    prev,
                    next
                });
                controlsDomestic.City.setValue(null);
            });
        // clear the location when countryarea changes
        controlsForeign.CountryArea.valueChanges
            .pipe(
                takeWhile(() => this.active),
                startWith(this.selectedCountryArea),
                tap(country =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipComponent:controlsForeign.CountryArea.valueChanges',
                        country
                    })
                ),
                pairwise(),
                tap(([prev, next]) =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipComponent:controlsForeign.CountryArea.valueChanges',
                        prev,
                        next
                    })
                ),
                filter(([prev, next]) => !!prev && !!next),
                filter(([prev, next]) => prev !== next)
            )
            .subscribe(([prev, next]) => {
                this.log.verbose({
                    type: 'ExpenseDetailCityStateZipComponent:controlsForeign.CountryArea.valueChanges',
                    prev,
                    next
                });
                controlsForeign.Location.setValue(null);
            });
        // clear the location when countryarea changes
        controlsOCONUS.CountryArea.valueChanges
            .pipe(
                takeWhile(() => this.active),
                startWith(this.selectedCountryArea),
                tap(area =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipComponent:controlsOCONUS.CountryArea.valueChanges',
                        area
                    })
                ),
                pairwise(),
                tap(([prev, next]) =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipComponent:controlsOCONUS.CountryArea.valueChanges',
                        prev,
                        next
                    })
                ),
                filter(([prev, next]) => !!prev && !!next),
                filter(([prev, next]) => prev !== next)
            )
            .subscribe(([prev, next]) => {
                this.log.verbose({
                    type: 'ExpenseDetailCityStateZipComponent:ontrolsOCONUS.CountryArea.valueChanges',
                    prev,
                    next
                });
                controlsOCONUS.Location.setValue(null);
            });
    }
}
